"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AluumeMark } from "@aluume/shared/components/brand/AluumeMark";

const BOOKING_NAV = [
  { href: "/bathe", label: "Bathe" },
  { href: "https://www.aluume.com/thermae-floating-sauna", label: "Thermae", external: true },
  { href: "/vouchers", label: "Gift Vouchers" },
];

function navLinkCls(active: boolean) {
  return `ui-label transition-colors ${active ? "text-ink-on-dark underline underline-offset-4 decoration-1" : "text-ink-on-dark/75 hover:text-ink-on-dark"}`;
}

function ProfileButton() {
  return (
    <Link
      href="/agent"
      aria-label="Profile"
      title="Profile"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-ink-on-dark/40 text-ink-on-dark transition-colors hover:border-ink-on-dark hover:bg-ink-on-dark/10"
    >
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" strokeLinecap="round" />
      </svg>
    </Link>
  );
}

// Fixed scroll-aware header.
//   startTransparent=true  → transparent at top (overlay hero), solid once scrolled
//   startTransparent=false → always solid (utility pages)
//   Hides on scroll-down past 60px, reappears solid on scroll-up.
export function ScrollHeader({ startTransparent = false }: { startTransparent?: boolean }) {
  const pathname = usePathname();
  const [atTop, setAtTop] = useState(true);
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);

  useEffect(() => {
    const update = (y: number, prev: number) => {
      setAtTop(y < 10);
      if (y > prev && y > 60) {
        setVisible(false);
      } else {
        setVisible(true);
      }
    };

    update(window.scrollY, window.scrollY);
    lastY.current = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      update(y, lastY.current);
      lastY.current = y;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const transparent = startTransparent && atTop;

  return (
    <header
      className={[
        "fixed left-0 right-0 top-0 z-50 flex w-full items-center justify-between px-6 text-ink-on-dark transition-[transform,background-color] duration-300 md:px-12",
        "py-3 md:py-[35px]",
        transparent ? "" : "shadow-[0_2px_12px_rgba(57,51,49,0.22)]",
        visible ? "translate-y-0" : "-translate-y-full",
      ].join(" ")}
      style={{ backgroundColor: transparent ? "transparent" : "#393331" }}
    >
      <a href="https://www.aluume.com" aria-label="Aluume home" className="shrink-0">
        <AluumeMark className="h-9 w-auto" />
      </a>

      {/* Desktop nav */}
      <nav className="hidden items-center gap-7 md:flex">
        {BOOKING_NAV.map((n) => n.external
          ? <a key={n.href} href={n.href} className={navLinkCls(false)}>{n.label}</a>
          : <Link key={n.href} href={n.href} className={navLinkCls(pathname === n.href)}>{n.label}</Link>
        )}
        <ProfileButton />
      </nav>

      {/* Mobile: hamburger only — the profile/agent-portal entry is desktop-only */}
      <div className="flex items-center gap-3 md:hidden">
        <details className="relative">
          <summary className="flex h-9 w-9 cursor-pointer list-none items-center justify-center text-[20px] leading-none text-ink-on-dark [&::-webkit-details-marker]:hidden">☰</summary>
          <nav className="absolute right-0 top-12 z-30 flex w-56 flex-col gap-3 rounded-lg bg-cream p-5 text-left shadow-[0px_18px_40px_-12px_rgba(57,51,49,0.28)]">
            {BOOKING_NAV.map((n) => n.external
              ? <a key={n.href} href={n.href} className="ui-label text-ink-soft hover:text-ink">{n.label}</a>
              : <Link key={n.href} href={n.href} className={`ui-label hover:text-ink ${pathname === n.href ? "text-ink underline underline-offset-4 decoration-1" : "text-ink-soft"}`}>{n.label}</Link>
            )}
          </nav>
        </details>
      </div>
    </header>
  );
}
